import { Popover } from 'antd'
import TooltipWrapper from '$components/Tooltip'
import { useTranslation } from 'react-i18next'
import { centToEuro } from '$components/OrderPartsListTable/utils'

export const translate = (obj, t) => {
  if (typeof obj === 'string') {
    return t(obj)
  }
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, translate(value, t)]),
    )
  }
  return obj
}

export const numberRenderer =
  (t, params = {}) =>
  (value) => {
    if (typeof value === 'undefined') {
      return ''
    }
    return t('localized_number', {
      val: value,
      ...params,
    })
  }

export const currencyRenderer = (t) => (value) =>
  t('localized_currency', {
    val: centToEuro(value || 0),
  })

export const dateWithTimeRenderer = (t) =>
  function DateWithTimeRenderer(value) {
    if (!value) {
      return null
    }

    const date = t('localized_datetime', {
      val: new Date(value),
      formatParams: {
        val: {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
      },
    })
    const datetime = t('localized_datetime', {
      val: new Date(value),
      formatParams: {
        val: {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
        },
      },
    })
    return (
      <Popover class="datetime" content={datetime}>
        {date}
      </Popover>
    )
  }

export const dateRenderer = (t) => (value) => {
  if (!value) {
    return null
  }

  const date = t('localized_datetime', {
    val: new Date(value),
    formatParams: {
      val: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      },
    },
  })
  return date
}

export const CurrencyWithoutUnit = ({
  value,
  tooltipText = '',
  style = {},
}) => {
  const { t } = useTranslation()

  if (typeof value === 'undefined') {
    return ''
  }

  return (
    <TooltipWrapper title={tooltipText}>
      <div style={style}>
        {t('localized_number', {
          val: value,
          minimumFractionDigits: 2,
        })}
      </div>
    </TooltipWrapper>
  )
}

export const DateTime = ({ value, withoutTime = false }) => {
  const { t } = useTranslation()

  if (!value) {
    return null
  }

  const val = new Date(value)

  let format = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
  }
  if (withoutTime) {
    format = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }
  }

  return t('localized_datetime', {
    val,
    formatParams: {
      val: format,
    },
  })
}

export const localeParseFloat = (val, i18n) => {
  const locale = i18n.language
  // Determine the correct decimal separator based on the locale
  const decimalSeparator = locale === 'en' ? '.' : ','
  const thousandsSeparator = locale === 'en' ? ',' : '.'

  // Remove thousand separators, and put a point where the decimal separator occurs
  const str = Array.from(val, (c) =>
    c === thousandsSeparator ? '' : c === decimalSeparator ? '.' : c,
  ).join('')

  // Now it can be parsed
  return Number.parseFloat(str)
}

export const getLanguageSwitcherOptions = (
  i18nInstance,
  includeCiModeInDev = true,
) => {
  let languagesToShow = ['en']
  if (
    i18nInstance &&
    i18nInstance.options &&
    Array.isArray(i18nInstance.options.supportedLngs)
  ) {
    languagesToShow = [...i18nInstance.options.supportedLngs]
  } else {
    console.warn(
      'i18n instance or supportedLngs not available, defaulting to English for language switcher.',
    )
  }

  let options = languagesToShow
    .filter((lng) => lng !== 'cimode')
    .map((lng) => ({
      label: lng.toUpperCase(),
      value: lng,
    }))

  if (includeCiModeInDev && process.env.NODE_ENV !== 'production') {
    options.push({ label: 'FX', value: 'cimode' })
  }

  return options
}

export const getColorLabel = (color, t) => {
  if (!color) return ''
  const colorKey = `my_materials.colors.${color.toLowerCase()}`
  return ` (${t(colorKey, color)})`
}
